import { ISchedulerItem, ISegmentationItem } from '@core/js/utils/segmentation/types';

export interface ILiveGameConfig {
    dealerName: string;
    dynamicImageUrl: string;
    isScheduled: boolean;
    language: string;
    launchAlias: string;
    limits: string;
    name: string;
    openingDateTime: string;
    seatsInfo: string;
}

export type TGameDemoModeSupport = 'both' | 'unavailable' | 'guest' | 'user';

export interface IGame {
    isBlocked?: boolean; /* TotalCasino changes */
    additionalAssets: IGameAsset[];
    articleId: string;
    bingoGameType: string;
    browserAvailability: string[];
    defaultGameImage: string;
    demoModeSupport: TGameDemoModeSupport;
    description: string;
    desktopGameType: string;
    deviceAvailability: string[];
    gameCode: string;
    gameCustomFieldsData: IGameCustomFieldData[];
    gameLabels: IGameLabel[];
    gameMode: 'maintenance' | 'default';
    gameProvider: string;
    gameTileAssetsLandscape: IGameTileAssetLayout;
    gameTileAssetsPortrait: IGameTileAssetLayout;
    gameTileAssetsSquare: IGameTileAssetLayout;
    isActive: boolean;
    isExcludedFromPGG: boolean;
    isExcludedFromSitemap: boolean;
    isGameExclusive: boolean;
    isGameHot: boolean;
    isGameNew: boolean;
    isGamePopular: boolean;
    jackpotCode: string;
    liveLaunchAlias: string;
    mobileArticleId: string;
    mobileGameCode: string;
    mobileGameType: string;
    name: string;
    osAvailability: string[];
    seoFriendlyGameName: string;
    urlCustomParameters: string;
    vfGameType: string;
    integrationType?: string;
}

export interface IGameAsset {
    name: string;
    url: string;
}

export interface IGameTileAssetLayout {
    layout1x1: IGameTileAsset;
    layout1x2: IGameTileAsset;
    layout2x1: IGameTileAsset;
    layout2x2: IGameTileAsset;
}

export interface IGameTileAsset {
    guestMainImage: string;
    guestMobileImage: string;
    mainImage: string;
    mobileImage: string;
}

export interface IGameLabel {
    name: string;
    isVisible: boolean;
}

export interface IGameCustomFieldData {
    name: string;
    tpe: 'text' | 'webContent' | 'image';
    value: string;
}

export interface IGamesGridLayoutResponse {
    columns: string;
    id: string;
    layout: string;
    name: string;
    rows: string;
    tileGap: string;
    tileRadius: string;
    tileSize: TGamesGridLayoutSize;
}

export enum LAYOUT_SIZES {
    landscape = 'landscape',
    portrait = 'portrait',
    square = 'square'
}

export type TGamesGridLayoutSize = keyof typeof LAYOUT_SIZES;

export interface IGamesFeedResponse {
    name: string;
    gamesFeedCategories: IGamesFeedCategoryResponse[];
    gamesGridLayouts: {
        [key: string]: IGamesGridLayoutResponse;
    };
}

export interface IGamesFeedCategoryResponse {
    name: string;
    id: string;
    seoFriendlyCategoryName: string;
    desktopCategoryConfiguration: IGamesCategoryConfigurationResponse;
    tabletCategoryConfiguration: IGamesCategoryConfigurationResponse;
    mobileCategoryConfiguration: IGamesCategoryConfigurationResponse;
    // scheduler
    isEnabledForGuest: boolean;
    isEnabledForLoggedIn: boolean;
    schedulerTimeZone: string;
    scheduler: ISchedulerItem[][];
    guestSegmentation: ISegmentationItem[][];
    loggedInSegmentation: ISegmentationItem[][];
}

export type TGamesGridLayout = IGamesGridLayoutResponse & {
    layoutTiles: string[];
    bordersCount: number;
}

export interface IGamesFeed {
    name: string;
    gamesFeedCategories: TGamesFeedCategory[];
    gamesGridLayouts: {
        [key: string]: TGamesGridLayout;
    };
}

export type TGamesFeedCategory = IGamesFeedCategoryResponse & {
    categoryConfiguration: TGamesCategoryConfiguration;
}

export enum CATEGORY_VIEWS {
    grid = 'grid',
    slider = 'slider',
    webcontent = 'webcontent',
    carousel = 'carousel',
    top = 'top'
}

export enum CATEGORY_TYPES {
    default = 'default',
    pgg = 'pgg',
    favorites = 'favorites',
    recent = 'recent',
    custom = 'custom'
}

export interface IGamesCategoryConfigurationResponse {
    categoryDescription: string;
    categoryGameGridLayout: string;
    categoryGames: string[];
    categoryIcon: string;
    categoryType: keyof typeof CATEGORY_TYPES;
    categoryView: keyof typeof CATEGORY_VIEWS;
    categoryWebContent: string;
    isActive: boolean;
    isShownCategoryName: boolean;
    isShownSliderArrows: boolean;
    isShownViewMore: boolean;
    isShownViewMoreGamesNumber: boolean;
    layoutsNumber: string;
    pggCategoryName: string;
    pggMaxGames: string;
    pggMinGames: string;
    slidesNumber: string;
    viewMoreGameGridLayout: string;
    carouselTileSize: TGamesGridLayoutSize;
    carouselGamesNumber: string;
    carouselGamesNumberInRow: string;
}

export type TGamesCategoryConfiguration = IGamesCategoryConfigurationResponse & {
    viewMoreGameGridLayout: TGamesGridLayout;
    categoryGameGridLayout: TGamesGridLayout;
}

export interface IGameType {
    demoPlayUrl: string;
    desktop: string;
    launchMethodDesktop: string;
    launchMethodMobile: string;
    mobile: string;
    name: string;
    previewDemoPlayUrl: string;
    previewRealPlayUrl: string;
    realPlayUrl: string;
}

export interface IGameBasicTileLayout {
    name: string;
    isLabelsEnabled: boolean;
    isJackpotEnabled: boolean;
    isGameNameEnabled: boolean;
    isProviderNameEnabled: boolean;
    isJackpotHitTimeEnabled: boolean;
    // game tile hover types
    gameTileLayoutHoverType: 'custom' | 'default';
    isDemoModeEnabled: boolean;
    isFavoritesEnabled: boolean;
    isGameInfoEnabled: boolean;
    isHoverGameNameEnabled: boolean;
    gameTileLayoutHoverCustomTemplate: IGameCustomTemplate[];
}

export interface IGameLiveTileLayout {
    name: string;
    isLimitsEnabled: boolean;
    isLabelsEnabled: boolean;
    isJackpotEnabled: boolean;
    isGameNameEnabled: boolean;
    isLanguageEnabled: boolean;
    isLiveImageEnabled: boolean;
    isDealerNameEnabled: boolean;
    isProviderNameEnabled: boolean;
    isPlayersNumberEnabled: boolean;
    isJackpotHitTimeEnabled: boolean;
    // game tile hover types
    gameTileLayoutHoverType: 'custom' | 'default';
    isFavoritesEnabled: boolean;
    isGameInfoEnabled: boolean;
    isHoverGameNameEnabled: boolean;
    gameTileLayoutHoverCustomTemplate: IGameCustomTemplate[];
}

export interface IGameCustomTemplate {
    lang: string;
    value: string;
}

export interface IGameInfoLayout {
    layout: IGameCustomTemplate[];
}
